import { Stack, Heading, HStack, Skeleton, Button, Text } from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { FiArrowRight } from 'react-icons/fi';
import { IoLeafOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router';

import AppCard from 'components/UI/AppCard';
import { FeaturedProductWithType } from 'hooks/data/search/types';
import useCategoryData from 'hooks/data/search/useCategories';
import useVersionInfo from 'hooks/useVersionInfo';
import { knownSectorCategories } from 'pages/Landing/content';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';

import FeaturedDatasetCard from '../FeaturedDatasetCard';
import SectorBadges from '../SectorBadges';
import SectorIcon from '../SectorIcon';

type Props = {
  selectedSubSectors: string[];
  setSelectedSubSectors: (sectors: string[]) => void;
  isLoading: boolean;
  data: FeaturedProductWithType[] | undefined;
};

const ExploreDatasets = ({ selectedSubSectors, setSelectedSubSectors, isLoading, data }: Props) => {
  const { ver, system_model } = useVersionInfo();

  const { data: categoryData } = useCategoryData();

  const navigate = useNavigate();

  const browseMoreDataset = () => {
    const baseUrl = new URL(`${window.location.href}/search`);
    selectedSubSectors.forEach((subSector) => baseUrl.searchParams.append('sector', subSector));
    const newUrl = baseUrl.href.replace(window.location.origin, '');
    mixpanel.track(MIXPANEL_EVENTS.EXPLORE_ALL_DATASETS, { selectedSubSectors });
    navigate(newUrl);
  };

  return (
    <AppCard width="100%" justifyContent="space-between" alignItems="start">
      <Stack>
        <Heading size="md" fontSize={'2xl'}>
          Explore the database via sectors
        </Heading>
        <Text>
          Our database contains a broad range of different activities, divided across industrial sectors. Select a sector to
          browse its contents.
        </Text>
      </Stack>
      <HStack alignItems="start" spacing={8} flexWrap="wrap">
        {categoryData?.map((category) => (
          <SectorBadges
            key={category.name}
            sector={category.name}
            subsectors={category.sectors}
            icon={<SectorIcon icon={knownSectorCategories[category.name]?.icon ?? IoLeafOutline} />}
            selectedSubSectors={selectedSubSectors}
            onSelectedSubSectorsChange={setSelectedSubSectors}
          />
        ))}
      </HStack>
      <Stack width={'100%'}>
        <HStack
          alignItems="start"
          display="grid"
          gridTemplateColumns={{ base: 'repeat(1, 1fr)', lg: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }}
          gridTemplateRows="auto"
        >
          {isLoading
            ? new Array(9).fill(0).map((_, index) => <Skeleton key={index} height="120px" isLoaded={false} />)
            : data &&
              data?.map((featuredDataset) => (
                <FeaturedDatasetCard
                  key={featuredDataset.dataset_id}
                  name={featuredDataset.activity}
                  refProduct={featuredDataset.product}
                  geography={featuredDataset.geography}
                  spoldId={featuredDataset.dataset_id}
                  badgeText={featuredDataset.type}
                  version={ver}
                  systemModel={system_model}
                  selectedSubSectors={selectedSubSectors}
                  type="featured"
                />
              ))}
        </HStack>
        {selectedSubSectors.length > 0 && (
          <Button
            mt={4}
            variant="link"
            size="sm"
            colorScheme="green"
            onClick={() => browseMoreDataset()}
            rightIcon={<FiArrowRight />}
            alignSelf={'flex-end'}
          >
            Browse more for {selectedSubSectors.length > 1 ? 'these sectors' : 'this sector'}
          </Button>
        )}
      </Stack>
    </AppCard>
  );
};

export default ExploreDatasets;
